
import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  ConfigProvider,
  Empty,
  Input,
  message as antMessage,
  Pagination,
  Popconfirm,
  Segmented,
  Space,
  Spin,
  Typography,
} from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  MessageOutlined,
  ReloadOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import profileIcon from "../assets/profile.png";
import { PublicProfile, SKILL_AREAS, SkillArea } from "../firebase-chat";
import { AddFriend, MAX_FRIENDS, RemoveFriend } from "../firebase-friends";
import { usePublicProfiles } from "../hooks/useChat";
import useFriends from "../hooks/useFriends";
import { normalizeText } from "../utils/xpImport";
import { SKILL_ICON_STYLE } from "../utils/skillIcons";

type Tab = "todos" | "amigos";
type SkillFilter = "Todas" | SkillArea;

const USERS_PER_PAGE = 12;

const segmentedTheme = {
  components: {
    Segmented: {
      trackBg: "transparent",
      itemColor: "var(--dashboard-header-blue)",
      itemHoverColor: "var(--dashboard-header-blue)",
      itemHoverBg: "rgba(47, 43, 116, 0.08)",
      itemSelectedBg: "rgba(47, 43, 116, 0.1)",
      itemSelectedColor: "var(--dashboard-header-blue)",
      borderRadiusSM: 10,
      borderRadius: 10,
    },
  },
};

const cardStyle: React.CSSProperties = {
  borderRadius: 24,
  border: "1px solid var(--dashboard-panel-border)",
  boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
};

const Community: React.FC = () => {
  const { user } = useAuth();
  const myUid = user?.uid ?? "";
  const navigate = useNavigate();

  const { profiles, byId, isLoading: profilesLoading, refresh } = usePublicProfiles();
  const { friendIds, friendSet, isLoading: friendsLoading } = useFriends(myUid);

  const [tab, setTab] = useState<Tab>("todos");
  const [search, setSearch] = useState("");
  const [skillFilter, setSkillFilter] = useState<SkillFilter>("Todas");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const matchesFilters = (p: PublicProfile) => {
    const q = normalizeText(search);
    if (q && !normalizeText(p.Nombre).includes(q)) return false;
    if (skillFilter !== "Todas" && p.MejorSkill !== skillFilter) return false;
    return true;
  };

  const everyone = useMemo(
    () => profiles.filter((p) => p.id !== myUid && matchesFilters(p)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profiles, myUid, search, skillFilter],
  );

  const friends = useMemo(
    () =>
      friendIds
        .map(
          (id): PublicProfile =>
            byId.get(id) ?? { id, Nombre: "Usuario no disponible", PortraitImg: "", Nivel: 0, MejorSkill: "" },
        )
        .filter(matchesFilters)
        .sort((a, b) => a.Nombre.localeCompare(b.Nombre)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [friendIds, byId, search, skillFilter],
  );

  const handleAdd = async (p: PublicProfile) => {
    if (friendIds.length >= MAX_FRIENDS) {
      antMessage.warning(`Podés tener hasta ${MAX_FRIENDS} amigos.`);
      return;
    }
    setBusyId(p.id);
    try {
      await AddFriend(myUid, p.id);
      antMessage.success(`${p.Nombre} ahora está en tus amigos.`);
    } catch (error) {
      console.error(error);
      antMessage.error("No se pudo agregar. Probá de nuevo.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (p: PublicProfile) => {
    setBusyId(p.id);
    try {
      await RemoveFriend(myUid, p.id);
      antMessage.success(`Eliminaste a ${p.Nombre} de tus amigos.`);
    } catch (error) {
      console.error(error);
      antMessage.error("No se pudo eliminar. Probá de nuevo.");
    } finally {
      setBusyId(null);
    }
  };

  const openChat = (p: PublicProfile) => navigate(`/chat?con=${p.id}`);

  const list = tab === "todos" ? everyone : friends;
  const loading = profilesLoading || (tab === "amigos" && friendsLoading);

  useEffect(() => {
    setPage(1);
  }, [tab, search, skillFilter]);

  const pagedList = useMemo(
    () => list.slice((page - 1) * USERS_PER_PAGE, page * USERS_PER_PAGE),
    [list, page],
  );

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1300, margin: "0 auto", width: "100%" }}>
        <Card className="app-glass" style={cardStyle} bodyStyle={{ padding: 24 }}>
          <Typography.Title level={2} style={{ margin: 0, color: "var(--dashboard-header-blue)" }}>
            Comunidad
          </Typography.Title>
          <Typography.Paragraph style={{ margin: "6px 0 0", color: "var(--dashboard-text)", maxWidth: 680 }}>
            Encontrá compañeros según su mejor skill, sumalos a tus amigos y
            escribiles para armar equipo.
          </Typography.Paragraph>
        </Card>

        <Card className="app-glass" style={{ ...cardStyle, marginTop: 16 }} bodyStyle={{ padding: 20 }}>
          <ConfigProvider theme={segmentedTheme}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
              <Segmented
                value={tab}
                onChange={(v) => setTab(v as Tab)}
                options={[
                  { label: "Todos", value: "todos" },
                  { label: `Mis amigos (${friendIds.length})`, value: "amigos" },
                ]}
              />
              <Space wrap>
                <Input.Search
                  placeholder="Buscar por nombre"
                  allowClear
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 240 }}
                />
                <Button icon={<ReloadOutlined />} onClick={() => void refresh()} aria-label="Actualizar lista" />
              </Space>
            </Space>

            <Segmented
              value={skillFilter}
              onChange={(v) => setSkillFilter(v as SkillFilter)}
              options={["Todas", ...SKILL_AREAS]}
            />

            {loading ? (
              <Spin style={{ display: "block", margin: "40px auto" }} />
            ) : list.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  tab === "amigos" && friendIds.length === 0
                    ? "Todavía no agregaste amigos. Buscalos en la pestaña Todos."
                    : "Nadie coincide con la búsqueda."
                }
              />
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                    gap: 16,
                  }}
                >
                  {pagedList.map((p) => (
                    <PersonCard
                      key={p.id}
                      profile={p}
                      isFriend={friendSet.has(p.id)}
                      mode={tab}
                      busy={busyId === p.id}
                      onAdd={() => void handleAdd(p)}
                      onRemove={() => void handleRemove(p)}
                      onMessage={() => openChat(p)}
                    />
                  ))}
                </div>

                {list.length > USERS_PER_PAGE && (
                  <Pagination
                    current={page}
                    total={list.length}
                    pageSize={USERS_PER_PAGE}
                    onChange={setPage}
                    align="center"
                    showSizeChanger={false}
                    style={{ marginTop: 8 }}
                  />
                )}
              </>
            )}
          </Space>
          </ConfigProvider>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// ─────────────────────────── Tarjeta de persona ───────────────────────────

const PersonCard: React.FC<{
  profile: PublicProfile;
  isFriend: boolean;
  mode: Tab;
  busy: boolean;
  onAdd: () => void;
  onRemove: () => void;
  onMessage: () => void;
}> = ({ profile, isFriend, mode, busy, onAdd, onRemove, onMessage }) => {
  const skill = profile.MejorSkill ? SKILL_ICON_STYLE[profile.MejorSkill] : null;
  const unavailable = profile.Nivel === 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        padding: "22px 20px 20px",
        borderRadius: 22,
        background: "var(--dashboard-panel-bg)",
        border: "1px solid var(--dashboard-section-blue)",
        // La franja de arriba toma el color de la mejor skill.
        borderTop: `5px solid ${skill?.color ?? "var(--dashboard-track)"}`,
        textAlign: "center",
      }}
    >
      <Avatar size={76} src={profile.PortraitImg || profileIcon} style={{ background: "#fff4c4" }} />
      <Typography.Text
        strong
        ellipsis={{ tooltip: profile.Nombre }}
        style={{ color: "var(--dashboard-header-blue)", fontSize: 17, maxWidth: "100%" }}
      >
        {profile.Nombre}
      </Typography.Text>

      {!unavailable && (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Typography.Text style={{ fontSize: 13, color: "var(--dashboard-text)" }}>
            Nivel {profile.Nivel}
          </Typography.Text>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "8px 14px",
              borderRadius: 14,
              fontWeight: 700,
              color: skill ? "var(--dashboard-white)" : "var(--dashboard-text)",
              background: skill?.color ?? "var(--dashboard-panel-soft)",
            }}
          >
            {skill?.icon}
            {profile.MejorSkill || "Sin skill aún"}
          </div>
        </Space>
      )}

      <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 4 }}>
        {!unavailable && (
          <Button block icon={<MessageOutlined />} onClick={onMessage}>
            Mensaje
          </Button>
        )}
        {mode === "amigos" ? (
          <Popconfirm
            title={`¿Eliminar a ${profile.Nombre} de tus amigos?`}
            description="Las conversaciones que tengan no se borran."
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={onRemove}
          >
            <Button block danger icon={<DeleteOutlined />} loading={busy}>
              Eliminar
            </Button>
          </Popconfirm>
        ) : isFriend ? (
          <Button block icon={<CheckOutlined />} disabled>
            Amigo
          </Button>
        ) : (
          <Button block type="primary" icon={<UserAddOutlined />} loading={busy} onClick={onAdd}>
            Agregar
          </Button>
        )}
      </Space>
    </div>
  );
};

export default Community;
